/**
 * The Pulse-Omics ring, straight from Figma 7469:631.
 *
 * Generated, not hand-typed. Every chord below is the designer's own exported
 * path, translated out of its individual placement box into the frame's single
 * coordinate space; each one's endpoints land within 0.07px of the node dots
 * they join, which is the check that the transform is right.
 *
 * The design is an exact circle — every dot sits on radius 336 around
 * (650, 546) to four decimal places. The nine are no longer evenly spaced:
 * they run at 36.89 degrees apart inside a group and 50.88 between the two,
 * which is what opens the break the tier arcs sit in. See TIERS.
 *
 * Depth comes from three tiers the designer set per chord — 0.6/1.7,
 * 0.34/1.15, 0.2/0.9 opacity over stroke-width — across two stroke colours.
 * That is what keeps the bundle from reading as one flat mesh, so the values
 * are carried through per chord rather than averaged into a single style.
 */

/** The design frame. Everything below is expressed in it. */
export const RING_FRAME = { w: 1300, h: 1040 };
export const RING_CENTRE = { x: 650, y: 546 };
export const RING_RADIUS = 336;

/**
 * The hairline circle the dots sit on.
 *
 * The design draws it at 7% of near-black on white. On this section's ground
 * that inverts to white, and a little stronger — 7% of white over #0a0c12 is
 * under two levels of separation and disappears at any real viewing distance.
 */
export const RIM = { r: 335.2, width: 1.6, stroke: 'rgba(255, 255, 255, 0.1)' };

/**
 * The two outer arcs, and the reason the ring is no longer evenly spaced.
 *
 * They group the nine systems into what the programme measures today and what
 * it is still exploring, and the 6 degree break between them is the only thing
 * on the drawing that says the two are different. Each arc runs its group's
 * own span plus 22.44 degrees at either end, which is what centres the break
 * in the wider node gap rather than leaving it against one shoulder.
 *
 * Radius 352 puts them 16 clear of the rim, with the labels a further 18 out.
 */
export const TIER_RADIUS = 352;
/* 2.8, against the design's 3.5. The arcs are a grouping mark around the
   drawing, not part of it, and at the frame's own weight they were competing
   with the chords they enclose. */
export const TIER_WIDTH = 2.8;
export const TIER_OPACITY = 0.9;

/**
 * Where each arc starts and ends, clockwise from twelve o'clock.
 *
 * Read off the exported vectors, which are 61-point polylines — the radius
 * came back 352 to within 0.07 across every point of both, so they are arcs
 * and are rebuilt as arcs here. The narrow ring needs the same two spans at
 * its own radius, which is why these are the source and the paths below are
 * derived rather than the other way round.
 */
export const TIER_ARCS = [
  { from: 2.997, to: 121.667 },
  { from: 127.671, to: 357.004 },
];

/** An SVG arc between two angles measured clockwise from twelve o'clock. */
export const arcPath = (
  cx: number,
  cy: number,
  r: number,
  a: { from: number; to: number },
) => {
  const at = (deg: number) => {
    const rad = (deg * Math.PI) / 180;
    return [cx + r * Math.sin(rad), cy - r * Math.cos(rad)];
  };
  const [x1, y1] = at(a.from);
  const [x2, y2] = at(a.to);
  const large = a.to - a.from > 180 ? 1 : 0;
  return `M${x1.toFixed(2)} ${y1.toFixed(
    2,
  )}A${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
};

export const TIERS = [
  { label: 'Live today', stroke: '#1539F5' },
  { label: 'Exploratory', stroke: '#8CA0FF' },
].map((t, i) => ({
  ...t,
  d: arcPath(RING_CENTRE.x, RING_CENTRE.y, TIER_RADIUS, TIER_ARCS[i]),
}));

/** Node dot radius, from the design's 9px circles. */
export const NODE_R = 4.5;
/** The two node fills: the three the programme measures today, and the rest. */
export const NODE_FILL_DEEP = '#1539F5';
export const NODE_FILL_SOFT = '#8CA0FF';

export const LABEL_SIZE = 16;
export const LABEL_TRACKING = 0.2;

export type RingNode = {
  label: string;
  dot: { x: number; y: number };
  /** `y` is the vertical centre of the text, not its top. */
  text: { x: number; y: number; anchor: 'start' | 'middle' | 'end' };
  deep: boolean;
};

/**
 * Labels are placed radially from their own dot, at one clearance for all nine.
 *
 * This is the design's own scheme, not a correction of it: measure any of the
 * nine text boxes in 7469:631 and its anchor point — the leading edge, or the
 * centre where the label sits over the dot — is exactly 370 from the ring
 * centre, which is 34 beyond the dots. Deriving it here rather than carrying
 * nine hand-placed boxes keeps them fixed if the ring is ever re-cut.
 */
const LABEL_GAP = 34;
/**
 * How far off the vertical a node has to be before its label runs out sideways
 * instead of centring over the dot. Only Endocrine is inside this, at 0.12,
 * and only Endocrine is centred in the design; Cardiovascular is next at 0.43
 * and the design runs it out to the side.
 */
const AXIS_THRESHOLD = 0.3;

const RAW = [
  {
    label: 'Cardiovascular',
    dot: { x: 794.3576, y: 242.5912 },
    sin: 0.42964,
    cos: 0.903,
    deep: true,
  },
  {
    label: 'Metabolic',
    dot: { x: 947.5831, y: 389.9862 },
    sin: 0.88566,
    cos: 0.46433,
    deep: true,
  },
  {
    label: 'Nervous',
    dot: { x: 981.6569, y: 599.8487 },
    sin: 0.98707,
    cos: -0.16026,
    deep: true,
  },
  {
    label: 'Respiratory',
    dot: { x: 817.4354, y: 837.3098 },
    sin: 0.49832,
    cos: -0.86699,
    deep: false,
  },
  {
    label: 'Endocrine',
    dot: { x: 609.0519, y: 879.4955 },
    sin: -0.12187,
    cos: -0.99255,
    deep: false,
  },
  {
    label: 'Renal',
    dot: { x: 417.0639, y: 788.1503 },
    sin: -0.69326,
    cos: -0.72069,
    deep: false,
  },
  {
    label: 'Hepatic',
    dot: { x: 318.3431, y: 599.8487 },
    sin: -0.98707,
    cos: -0.16026,
    deep: false,
  },
  {
    label: 'Immune',
    dot: { x: 352.4169, y: 389.9862 },
    sin: -0.88566,
    cos: 0.46433,
    deep: false,
  },
  {
    label: 'Musculoskeletal',
    dot: { x: 505.6424, y: 242.5912 },
    sin: -0.42964,
    cos: 0.903,
    deep: false,
  },
] as const;

const place = (n: (typeof RAW)[number]): RingNode => {
  if (Math.abs(n.sin) <= AXIS_THRESHOLD) {
    return {
      label: n.label,
      dot: n.dot,
      text: {
        x: n.dot.x,
        y: n.dot.y + (n.cos > 0 ? -LABEL_GAP : LABEL_GAP),
        anchor: 'middle',
      },
      deep: n.deep,
    };
  }
  return {
    label: n.label,
    dot: n.dot,
    text: {
      x: n.dot.x + n.sin * LABEL_GAP,
      y: n.dot.y - n.cos * LABEL_GAP,
      anchor: n.sin > 0 ? 'start' : 'end',
    },
    deep: n.deep,
  };
};

export const RING_NODES: readonly RingNode[] = RAW.map(place);

/**
 * Each chord runs a gradient between the colours of the two nodes it joins,
 * rather than a single flat stroke.
 *
 * The design's chords are solid — checked three ways, including pixel-sampling
 * Figma's own render — and with only two node colours across nine nodes the
 * bundle came out reading as one blue at three opacities. Fading each wire
 * from one end's colour to the other's keeps every value the design chose and
 * adds the variation it was missing, and it earns its keep: the colour now
 * tells you which pair a chord belongs to, which position alone could not.
 */
export const nodeFill = (label: string) =>
  ['Cardiovascular', 'Metabolic', 'Nervous'].includes(label)
    ? NODE_FILL_DEEP
    : NODE_FILL_SOFT;

export type RingChord = {
  from: string;
  to: string;
  d: string;
  stroke: string;
  opacity: number;
  width: number;
};

export const RING_CHORDS: readonly RingChord[] = [
  {
    from: 'Cardiovascular',
    to: 'Respiratory',
    stroke: '#1539F5',
    opacity: 0.6,
    width: 1.7,
    d: 'M794.40 242.60C693.30 455.00 700.20 633.40 817.40 837.30',
  },
  {
    from: 'Cardiovascular',
    to: 'Nervous',
    stroke: '#1539F5',
    opacity: 0.6,
    width: 1.7,
    d: 'M794.40 242.60C684.60 473.20 729.60 558.90 981.70 599.80',
  },
  {
    from: 'Cardiovascular',
    to: 'Metabolic',
    stroke: '#1539F5',
    opacity: 0.6,
    width: 1.7,
    d: 'M794.40 242.60C699.10 442.80 751.20 493.00 947.60 390.00',
  },
  {
    from: 'Cardiovascular',
    to: 'Renal',
    stroke: '#1539F5',
    opacity: 0.34,
    width: 1.15,
    d: 'M794.40 242.60C689.00 464.10 587.10 611.40 417.10 788.20',
  },
  {
    from: 'Cardiovascular',
    to: 'Endocrine',
    stroke: '#1539F5',
    opacity: 0.34,
    width: 1.15,
    d: 'M794.40 242.60C694.80 451.90 637.30 649.40 609.10 879.50',
  },
  {
    from: 'Cardiovascular',
    to: 'Musculoskeletal',
    stroke: '#1539F5',
    opacity: 0.2,
    width: 0.9,
    d: 'M794.40 242.60C681.80 479.30 618.20 479.30 505.60 242.60',
  },
  {
    from: 'Respiratory',
    to: 'Nervous',
    stroke: '#8CA0FF',
    opacity: 0.6,
    width: 1.7,
    d: 'M817.40 837.30C700.20 633.40 749.50 562.20 981.70 599.80',
  },
  {
    from: 'Respiratory',
    to: 'Immune',
    stroke: '#8CA0FF',
    opacity: 0.2,
    width: 0.9,
    d: 'M817.40 837.30C690.20 615.90 578.60 508.60 352.40 390.00',
  },
  {
    from: 'Nervous',
    to: 'Endocrine',
    stroke: '#1539F5',
    opacity: 0.34,
    width: 1.15,
    d: 'M981.70 599.80C762.80 564.30 636.10 659.40 609.10 879.50',
  },
  {
    from: 'Nervous',
    to: 'Musculoskeletal',
    stroke: '#1539F5',
    opacity: 0.34,
    width: 1.15,
    d: 'M981.70 599.80C739.50 560.50 611.00 464.10 505.60 242.60',
  },
  {
    from: 'Nervous',
    to: 'Immune',
    stroke: '#1539F5',
    opacity: 0.2,
    width: 0.9,
    d: 'M981.70 599.80C752.80 562.70 557.70 497.60 352.40 390.00',
  },
  {
    from: 'Metabolic',
    to: 'Hepatic',
    stroke: '#1539F5',
    opacity: 0.6,
    width: 1.7,
    d: 'M947.60 390.00C715.50 511.70 577.00 557.80 318.30 599.80',
  },
  {
    from: 'Metabolic',
    to: 'Endocrine',
    stroke: '#1539F5',
    opacity: 0.6,
    width: 1.7,
    d: 'M947.60 390.00C739.30 499.20 637.70 646.00 609.10 879.50',
  },
  {
    from: 'Metabolic',
    to: 'Renal',
    stroke: '#1539F5',
    opacity: 0.34,
    width: 1.15,
    d: 'M947.60 390.00C721.40 508.60 594.10 604.10 417.10 788.20',
  },
  {
    from: 'Metabolic',
    to: 'Immune',
    stroke: '#1539F5',
    opacity: 0.34,
    width: 1.15,
    d: 'M947.60 390.00C751.20 493.00 548.80 493.00 352.40 390.00',
  },
  {
    from: 'Metabolic',
    to: 'Musculoskeletal',
    stroke: '#1539F5',
    opacity: 0.34,
    width: 1.15,
    d: 'M947.60 390.00C730.30 503.90 611.00 464.10 505.60 242.60',
  },
  {
    from: 'Endocrine',
    to: 'Renal',
    stroke: '#8CA0FF',
    opacity: 0.34,
    width: 1.15,
    d: 'M609.10 879.50C637.30 649.40 577.80 621.10 417.10 788.20',
  },
  {
    from: 'Endocrine',
    to: 'Immune',
    stroke: '#8CA0FF',
    opacity: 0.2,
    width: 0.9,
    d: 'M609.10 879.50C641.00 619.40 584.50 511.70 352.40 390.00',
  },
  {
    from: 'Renal',
    to: 'Hepatic',
    stroke: '#8CA0FF',
    opacity: 0.34,
    width: 1.15,
    d: 'M417.10 788.20C580.10 618.60 550.50 562.20 318.30 599.80',
  },
  {
    from: 'Hepatic',
    to: 'Immune',
    stroke: '#8CA0FF',
    opacity: 0.34,
    width: 1.15,
    d: 'M318.30 599.80C570.40 558.90 578.60 508.60 352.40 390.00',
  },
];
